/**
 * @author Martin Leonov <martin.leonov@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */
/* eslint-disable max-statements */

import AssertionError from 'assertion-error';

import { neverThrow } from '../../../util.type';
import BasicController from './basic-controller';
import HiddenController from './hidden-controller';
import ModernController from './modern-controller';
import UniversalController from './universal-controller';

/**
 * @import { ViewerManagerController } from '.'
 * @import { IAnalyticsTracker } from '../../analytics'
 * @import { ContentInfo } from '../../content/info/content-info'
 * @import { ContentTaskCronFactory } from '../../content/task/cron/content-task-cron-factory'
 * @import { Logger } from '../../logger/logger'
 * @import { ContentControllerFactory } from '../../content/controller/'
 */

/**
 * @private
 * @typedef {Record<string, *>} AppManager TODO #555
 * @typedef {Record<string, *>} ContentTaskCron TODO #555
 * @typedef {Record<string, *>} FingerprintManager TODO #555
 * @typedef {Record<string, *>} FirmwareManager TODO #555
 * @typedef {Record<string, *>} PowerManager TODO #555
 * @typedef {Record<string, *>} ReportInfo TODO #555
 */

export default class ViewerManager {
  /**
   * @param {IAnalyticsTracker} analyticsTracker
   * @param {AppManager} appManager
   * @param {ContentControllerFactory} contentControllerFactory
   * @param {ContentTaskCronFactory} contentTaskCronFactory
   * @param {Logger} logger
   */
  constructor(
    analyticsTracker,
    appManager,
    contentControllerFactory,
    contentTaskCronFactory,
    logger
  ) {
    this.analyticsTracker = analyticsTracker;
    this.appManager = appManager;
    this.contentControllerFactory = contentControllerFactory;
    this.contentTaskCronFactory = contentTaskCronFactory;
    this.logger = logger;

    /** @type {ViewerManagerController[]} */
    this.controllers = [];

    /** @type {HTMLElement | undefined} */
    this.el = undefined;
  }

  /**
   * @param {HTMLElement} el
   * @param {ContentInfo} contentInfo
   * @param {string} target
   * @returns {ViewerManagerController}
   */
  createHiddenController(el, contentInfo, target) {
    return new HiddenController(
      this.analyticsTracker,
      this.contentControllerFactory,
      contentInfo,
      this.contentTaskCronFactory,
      el,
      target
    );
  }

  /**
   * @param {HTMLElement | undefined} el
   */
  setEl(el) {
    this.el = el;
  }

  /**
   * Nastaví novou sadu aktuálně přehrávaných obsahových úloh.
   * Pokud je již nějaká existující sada aktuálně přehrávaných
   * obsahových úloh, tak je nejprve odstraní a zastaví jejich
   * přehrávání, stejně jako při nil contentInfo.
   *
   * Nepřehrává se více úloh zároveň, ale mohou se střídat v čase,
   * což zajišťuje CRON, který se zároveň spustí.
   * @param {ContentInfo | null | undefined} contentInfo
   */
  async setContentInfo(contentInfo) {
    if (!this.el) {
      throw new AssertionError('Root element is not set.');
    }
    this.logger.debug(
      `ViewerManager#setContentInfo ${contentInfo?.contentTimestamp}`
    );
    if (this.controllers.length > 0) {
      await Promise.all(this.controllers.map((c) => c.stop()));
      this.controllers = [];
    }

    if (!contentInfo) {
      return;
    }

    const hiddenTarget = 'hidden';
    if (hasContentTasksWithTarget(contentInfo, hiddenTarget)) {
      this.controllers.push(
        this.createHiddenController(this.el, contentInfo, hiddenTarget)
      );
    }
    /** @type {ViewerManagerController | undefined} */
    let controller;
    const { type } = contentInfo.visualController;
    switch (type) {
      case 'BasicController':
        controller = new BasicController(
          this.analyticsTracker,
          this.contentControllerFactory,
          contentInfo,
          this.contentTaskCronFactory,
          this.el
        );
        break;
      case 'UniversalController':
        controller = new UniversalController(
          this.analyticsTracker,
          this.appManager,
          this.contentControllerFactory,
          contentInfo,
          this.contentTaskCronFactory,
          this.el,
          this.logger
        );
        break;
      case 'ModernController':
        controller = new ModernController(
          this.analyticsTracker,
          this.appManager,
          this.contentControllerFactory,
          contentInfo,
          this.contentTaskCronFactory,
          this.el,
          this.logger
        );
        break;
      default:
        neverThrow(type, () => new RangeError(`Unknown controller "${type}".`));
    }
    this.controllers.push(controller);
    await Promise.all(this.controllers.map((c) => c.start()));
  }
}

/**
 * @param {ContentInfo} contentInfo
 * @param {string} target
 * @returns {boolean}
 */
function hasContentTasksWithTarget(contentInfo, target) {
  return (
    contentInfo.contentTasks.findIndex(
      (contentTask) => contentTask.target === target
    ) !== -1
  );
}
